// MappingWizard — maps CSV columns to Object Type properties.
// Used in Step 2 of UploadWizard.
//
// If a header-signature match is found the parent (UploadWizard) skips straight
// to step 3; this component handles the case where no match was found and the
// user must do manual mapping.
//
// Auto-apply notice is rendered by the parent; this component only renders the
// manual mapping UI.

import { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useObjectTypes, useObjectType } from "@/hooks/useObjectTypes";
import { useSaveMapping } from "@/hooks/useMappings";
import type {
  DataSource,
  DataSourceSchema,
  Mapping,
  ObjectType,
  PropertyType,
  SniffedType,
  PropertyKind,
} from "@/types/ontology";
import { headerMatchesApiName } from "@/lib/ontology/slugify";

// ── Type-compatibility check ────────────────────────────────────────────────

/**
 * Determine if a sniffed CSV column type is compatible with a property kind.
 * Returns null if compatible, a warning string if there is a mismatch.
 * Per spec §5: mismatches get a yellow warning — not an error.
 *
 * Exported for unit testing.
 */
export function typeMismatchWarning(sniffed: SniffedType, kind: PropertyKind): string | null {
  // Compatible mappings (no warning).
  const compatible: Record<SniffedType, PropertyKind[]> = {
    string: ["string", "enum", "ref"],
    number: ["number", "currency"],
    date: ["date"],
    bool: ["bool"],
  };

  if (compatible[sniffed]?.includes(kind)) return null;

  return `Source is ${sniffed}, target expects ${kind}`;
}

// ── Component ───────────────────────────────────────────────────────────────

export interface MappingWizardProps {
  headers: string[];
  schema: DataSourceSchema;
  dataSource: DataSource;
  autoAppliedMapping: Mapping | null;
  selectedObjectType: ObjectType | null;
  propertyTypes: PropertyType[];
  onObjectTypeSelected: (objectType: ObjectType, props: PropertyType[]) => void;
  onMappingSaved: (mapping: Mapping) => void;
}

export function MappingWizard({
  headers,
  schema,
  dataSource,
  autoAppliedMapping: _autoAppliedMapping,
  selectedObjectType,
  propertyTypes,
  onObjectTypeSelected,
  onMappingSaved,
}: MappingWizardProps) {
  const { objectTypes, isLoading: typesLoading } = useObjectTypes();
  const { objectType: loadedType, isLoading: typeLoading } = useObjectType(
    selectedObjectType?.id ?? null
  );
  const saveMapping = useSaveMapping();

  // column_map: property_api_name → source_column_name
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [primaryKeyColumn, setPrimaryKeyColumn] = useState<string>("");
  const [mappingName, setMappingName] = useState<string>(`${dataSource.name} mapping`);

  // Initialise mappings when the object type and its properties are loaded.
  useEffect(() => {
    if (!loadedType || loadedType.property_types.length === 0) return;

    // Auto-infer: match each property api_name against CSV headers.
    // Comparison is case- and space-insensitive: slugify the header and compare
    // to the api_name, so "Transaction ID" matches "transaction_id".
    const inferred: Record<string, string> = {};
    for (const prop of loadedType.property_types) {
      const matchedHeader = headers.find((h) => headerMatchesApiName(h, prop.api_name));
      if (matchedHeader) {
        inferred[prop.api_name] = matchedHeader;
      }
    }
    setColumnMappings(inferred);

    // Pre-select the first unique+required property as PK, or the first header.
    const pkProp = loadedType.property_types.find((p) => p.unique_in_type && p.required);
    const pkMatchedHeader = pkProp
      ? headers.find((h) => headerMatchesApiName(h, pkProp.api_name))
      : undefined;
    if (pkMatchedHeader) {
      setPrimaryKeyColumn(pkMatchedHeader);
    } else if (headers.length > 0) {
      setPrimaryKeyColumn(headers[0]);
    }
  }, [loadedType, headers]);

  const handleObjectTypeChange = async (id: string) => {
    const ot = objectTypes.find((o) => o.id === id);
    if (!ot) return;
    // We pass an empty props array initially; the useObjectType hook will load them.
    // The parent's onObjectTypeSelected will trigger the signature lookup.
    onObjectTypeSelected(ot, []);
  };

  const handleSave = async () => {
    if (!selectedObjectType || !primaryKeyColumn) return;

    const mapping = await saveMapping.mutateAsync({
      objectTypeId: selectedObjectType.id,
      name: mappingName,
      primaryKeyColumn,
      columnMap: columnMappings,
      headerSignature: schema.header_signature,
    });

    onMappingSaved(mapping);
  };

  const activePropertyTypes = loadedType?.property_types ?? propertyTypes;

  const getSniffedType = (header: string): SniffedType => {
    return schema.columns.find((c) => c.name === header)?.sniffed_type ?? "string";
  };

  return (
    // Flex column so the dialog/parent can bound our height: the body scrolls
    // internally (min-h-0 lets the flex child shrink) while the footer stays pinned.
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
        {/* Object Type selection */}
        <div className="space-y-2">
          <Label htmlFor="object-type-select" className="text-xs text-muted-foreground">
            Target Object Type
          </Label>
          {typesLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Select value={selectedObjectType?.id ?? ""} onValueChange={handleObjectTypeChange}>
              <SelectTrigger id="object-type-select">
                <SelectValue placeholder="Select an Object Type…" />
              </SelectTrigger>
              <SelectContent>
                {objectTypes.map((ot) => (
                  <SelectItem key={ot.id} value={ot.id}>
                    {ot.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Loading property types */}
        {selectedObjectType && typeLoading && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        )}

        {/* Column mapping form */}
        {selectedObjectType && !typeLoading && activePropertyTypes.length > 0 && (
          <>
            <Separator />

            <div className="space-y-3">
              <p className="text-sm font-medium">Column mapping</p>
              <p className="text-xs text-muted-foreground">
                Assign a CSV column to each property. Columns with matching names are pre-filled.
              </p>

              {/* Header row */}
              <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 text-xs text-muted-foreground px-1">
                <span>CSV Column</span>
                <span />
                <span>Object Type Property</span>
                <span />
              </div>

              {activePropertyTypes.map((prop) => {
                const mappedColumn = columnMappings[prop.api_name] ?? "";
                const sniffed = mappedColumn ? getSniffedType(mappedColumn) : null;
                const warning = sniffed ? typeMismatchWarning(sniffed, prop.kind) : null;

                return (
                  <div key={prop.id} className="space-y-1">
                    <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                      {/* CSV column select */}
                      <Select
                        value={mappedColumn}
                        onValueChange={(col) =>
                          setColumnMappings((prev) => ({
                            ...prev,
                            [prop.api_name]: col === "__none__" ? "" : col,
                          }))
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="— not mapped —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— not mapped —</SelectItem>
                          {headers.map((h) => (
                            <SelectItem key={h} value={h}>
                              <span className="font-mono">{h}</span>
                              {sniffed && mappedColumn === h && (
                                <span className="ml-1 text-muted-foreground">({sniffed})</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <span className="text-muted-foreground text-xs">→</span>

                      {/* Property name + kind */}
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono text-xs truncate">{prop.api_name}</span>
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1 py-0 h-4 flex-shrink-0"
                        >
                          {prop.kind}
                        </Badge>
                        {prop.required && (
                          <span className="text-destructive text-xs flex-shrink-0">*</span>
                        )}
                      </div>

                      {/* Warning icon placeholder */}
                      <div className="w-4 flex-shrink-0">
                        {warning && (
                          <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* Type mismatch warning — yellow, not an error */}
                    {warning && <p className="text-xs text-warning ml-1 pl-1">{warning}</p>}
                  </div>
                );
              })}
            </div>

            <Separator />

            {/* Primary key column */}
            <div className="space-y-2">
              <Label htmlFor="pk-column-select" className="text-xs text-muted-foreground">
                Primary key column
              </Label>
              <Select value={primaryKeyColumn} onValueChange={setPrimaryKeyColumn}>
                <SelectTrigger id="pk-column-select">
                  <SelectValue placeholder="Select primary key column…" />
                </SelectTrigger>
                <SelectContent>
                  {headers.map((h) => (
                    <SelectItem key={h} value={h}>
                      <span className="font-mono text-xs">{h}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mapping name */}
            <div className="space-y-2">
              <Label htmlFor="mapping-name" className="text-xs text-muted-foreground">
                Mapping name
              </Label>
              <Input
                id="mapping-name"
                value={mappingName}
                onChange={(e) => setMappingName(e.target.value)}
                placeholder="e.g. Vendor monthly export — May 2026"
              />
              <p className="text-xs text-muted-foreground">
                This mapping template will auto-apply on future uploads with the same column
                headers.
              </p>
            </div>
          </>
        )}

        {/* Empty object type state */}
        {selectedObjectType && !typeLoading && activePropertyTypes.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-6">
            This Object Type has no properties defined. Add properties in{" "}
            <a href="/ontology" className="text-primary underline">
              /ontology
            </a>{" "}
            first.
          </div>
        )}
      </div>

      {/* Pinned footer — stays visible while the body scrolls above it */}
      {selectedObjectType && !typeLoading && activePropertyTypes.length > 0 && (
        <div className="flex justify-end border-t border-border pt-4 mt-4 flex-shrink-0">
          <Button
            onClick={handleSave}
            disabled={!primaryKeyColumn || !mappingName.trim() || saveMapping.isPending}
          >
            {saveMapping.isPending ? "Saving…" : "Save mapping & preview"}
          </Button>
        </div>
      )}
    </div>
  );
}
